import React, { useState, useCallback } from "react";
import Badge from "../../shared/TailwindComponents/Badge";
import Button from "../../shared/TailwindComponents/Button";
import Input from "../../shared/TailwindComponents/Input";
import Textarea from "../../shared/TailwindComponents/Textarea";
import {
  PiTextAlignLeftLight,
  PiTagLight,
  PiFileLight,
  PiDatabaseLight,
  PiGlobeLight,
  PiCheckCircleLight,
  PiArrowsClockwiseLight,
  PiEyeLight,
  PiFileTextLight,
  PiDiamondsFourLight,
  PiCalendarBlankLight,
  PiClockClockwiseLight,
  PiCircleDashedLight,
  PiLightningLight,
  PiPencilLight,
  PiXLight,
  PiPlusLight,
} from "react-icons/pi";
import { formatDate, formatDateTime } from "../../../utils/functions";
import RecentActivityCard from "../../shared/RecentActivityCard";
import AuditLogPanel from "../../AuditLog/AuditLogPanel";
import { fetchEntityAuditLog } from "../../../store/actions/AuditLogActions";
import { useAppDispatch } from "../../../store";

/* ─── Shared types (exported for consumers) ─── */

export type AccessLevel = "view" | "edit" | "admin";

export type ShareUser = {
  id: string;
  name: string;
  email: string;
  role: string;
  initials: string;
  avatarUrl: string;
  access?: AccessLevel;
};

export type DataSource = {
  id: string;
  uuid?: string;
  /* Supports both API (snake_case) and legacy (PascalCase) field names */
  DataSourceName?: string;
  name?: string;
  DataSourceType?: "Personal" | "Shared" | "Company";
  visibility?: string;
  last_index_run?: string;
  CreatedAt?: string;
  created_at?: string;
  updated_at?: string;
  document_count?: number;
  total_storage_size?: string;
  total_size_bytes?: number;
  language?: string;
  description?: string;
  type?: string;
  status?: string;
  indexing_status?: string;
  indexer_status?: string;
  keywords?: string[];
  shared_with?: ShareUser[];
  collaborators?: any[];
  allowed_file_types?: string[] | string;
  last_indexed_at?: string | null;
  last_synced_at?: string | null;
  llm_generated_at?: string | null;
};

export type DataSourceDetailsProps = {
  loading?: boolean;
  dataSource?: DataSource;
  canShare?: boolean;
  canEdit?: boolean;
  onShareUpdate?: (nextShared: ShareUser[]) => Promise<void> | void;
  onRevokeAccess?: (id: string) => Promise<void> | void;
  onSave?: (updates: { name?: string; description?: string; type?: string; keywords?: string[] }) => Promise<boolean>;
  t?: (key: string) => string;
};

/* ─── Helpers ─── */

const formatSize = (bytes?: number) => {
  if (!bytes && bytes !== 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let i = -1;
  let size = bytes;
  do {
    size /= 1024;
    i++;
  } while (size >= 1024 && i < units.length - 1);
  return `${size.toFixed(1)} ${units[i]}`;
};

const visibilityLabel = (ds?: DataSource) => {
  if (ds?.DataSourceType) return ds.DataSourceType;
  const v = ds?.visibility;
  if (v === "private") return "Personal";
  if (v === "organization") return "Company";
  if (v === "shared") return "Shared";
  return v || "—";
};

type StatusCfg = { label: string; badge: string };
const statusCfg = (s?: string): StatusCfg => {
  const m: Record<string, StatusCfg> = {
    active:           { label: "Active",           badge: "green" },
    ready:            { label: "Ready",            badge: "green" },
    indexing:         { label: "Indexing",          badge: "blue" },
    pending:          { label: "Pending",           badge: "yellow" },
    pending_indexing: { label: "Pending Indexing",  badge: "yellow" },
    queued:           { label: "Queued",            badge: "yellow" },
    error:            { label: "Error",             badge: "red" },
    failed:           { label: "Failed",            badge: "red" },
    archived:         { label: "Archived",          badge: "gray" },
  };
  return m[s || ""] || { label: s || "Unknown", badge: "gray" };
};

const indexerCfg = (s?: string): { label: string; badge: string } => {
  const m: Record<string, { label: string; badge: string }> = {
    completed:        { label: "Completed",   badge: "green" },
    success:          { label: "Success",     badge: "green" },
    running:          { label: "Running",     badge: "blue" },
    transientFailure: { label: "Retrying",    badge: "yellow" },
    failed:           { label: "Failed",      badge: "red" },
    error:            { label: "Error",       badge: "red" },
    reset:            { label: "Reset",       badge: "purple" },
    notStarted:       { label: "Not Started", badge: "gray" },
  };
  return m[s || ""] || { label: s || "Not Started", badge: "gray" };
};

const parseFileTypes = (ft?: string[] | string): string[] => {
  if (!ft) return [];
  if (Array.isArray(ft)) return ft;
  return ft.split(",").map((s) => s.trim()).filter(Boolean);
};

/* ─── Main Panel ─── */

const DataSourceDetailsPanel: React.FC<DataSourceDetailsProps> = ({
  loading,
  dataSource: ds,
  canShare = true,
  canEdit = true,
  onShareUpdate,
  onRevokeAccess,
  onSave,
  t = (s) => s,
}) => {
  const dispatch = useAppDispatch();
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editType, setEditType] = useState("");
  const [editKeywords, setEditKeywords] = useState<string[]>([]);
  const [newKeyword, setNewKeyword] = useState("");

  const startEditing = useCallback(() => {
    setEditName(ds?.DataSourceName || ds?.name || "");
    setEditDescription(ds?.description || "");
    setEditType(ds?.type || "");
    setEditKeywords([...(ds?.keywords || [])]);
    setNewKeyword("");
    setIsEditing(true);
  }, [ds]);

  const cancelEditing = useCallback(() => {
    setIsEditing(false);
    setNewKeyword("");
  }, []);

  const handleSave = useCallback(async () => {
    if (!onSave) return;
    setIsSaving(true);
    try {
      const updates: { name?: string; description?: string; type?: string; keywords?: string[] } = {};
      const currentName = ds?.DataSourceName || ds?.name || "";
      if (editName !== currentName) updates.name = editName;
      if (editDescription !== (ds?.description || "")) updates.description = editDescription;
      if (editType !== (ds?.type || "")) updates.type = editType;
      if (JSON.stringify(editKeywords) !== JSON.stringify(ds?.keywords || [])) updates.keywords = editKeywords;

      if (Object.keys(updates).length > 0) {
        const ok = await onSave(updates);
        if (ok) setIsEditing(false);
      } else {
        setIsEditing(false);
      }
    } finally {
      setIsSaving(false);
    }
  }, [onSave, editName, editDescription, editType, editKeywords, ds]);

  const addKeyword = useCallback(() => {
    const kw = newKeyword.trim();
    if (kw && !editKeywords.includes(kw)) {
      setEditKeywords((prev) => [...prev, kw]);
      setNewKeyword("");
    }
  }, [newKeyword, editKeywords]);

  const removeKeyword = useCallback((kw: string) => {
    setEditKeywords((prev) => prev.filter((k) => k !== kw));
  }, []);
  /* ── Loading skeleton ── */
  if (loading) {
    return (
      <div className="w-full h-full overflow-y-auto p-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 space-y-5">
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm p-5 space-y-3">
              <div className="h-4 w-1/3 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
              <div className="h-3 w-2/3 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
              <div className="h-3 w-1/2 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm p-4 space-y-2">
                  <div className="h-3 w-16 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                  <div className="h-4 w-20 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                </div>
              ))}
            </div>
          </div>
          <div className="space-y-5">
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm p-5 space-y-3">
              <div className="h-4 w-1/3 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
              <div className="h-3 w-2/3 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  const st = statusCfg(ds?.status);
  const ix = indexerCfg(ds?.indexer_status || ds?.indexing_status);
  const fileTypes = parseFileTypes(ds?.allowed_file_types);

  return (
    <div className="w-full h-full overflow-y-auto p-4">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

        {/* ── Left Column ── */}
        <div className="lg:col-span-2 space-y-5">

          {/* ── Description Card ── */}
          <div data-tour-id="ds-details-description" className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm">
            <div className="flex justify-between items-center px-5 py-3.5 border-b border-gray-100 dark:border-gray-700/60">
              <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                <PiTextAlignLeftLight className="h-4 w-4" />
                <span className="text-xs font-medium">{t("Description")}</span>
              </div>
              {canEdit && !isEditing && (
                <Button
                  variant="primary"
                  size="small"
                  onClick={startEditing}
                  icon={<PiPencilLight className="h-3.5 w-3.5" />}
                >
                  {t("Edit")}
                </Button>
              )}
            </div>

            {isEditing && canEdit ? (
              <div className="p-5 space-y-4">
                <Input
                  type="text"
                  label={t("Data Source Name")}
                  placeholder={t("Enter data source name")}
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="w-full"
                />
                <Textarea
                  label={t("Description")}
                  placeholder={t("Describe what this data source contains...")}
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  className="w-full"
                  id="ds-description"
                />
                <Input
                  type="text"
                  label={t("Type / Category")}
                  placeholder={t("e.g. Legal Documents, Case Law, Contracts")}
                  value={editType}
                  onChange={(e) => setEditType(e.target.value)}
                  className="w-full"
                />

                {/* Keyword editor */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t("Keywords")}
                  </label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {editKeywords.map((kw, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-xs font-medium text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-700 capitalize"
                      >
                        {kw}
                        <button
                          type="button"
                          onClick={() => removeKeyword(kw)}
                          className="text-blue-400 hover:text-blue-600 dark:hover:text-blue-200"
                        >
                          <PiXLight className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2 items-end">
                    <Input
                      type="text"
                      value={newKeyword}
                      onChange={(e) => setNewKeyword(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addKeyword(); } }}
                      placeholder={t("Add a keyword...")}
                      className="flex-1"
                    />
                    <Button
                      variant="primary"
                      size="small"
                      onClick={addKeyword}
                      disabled={!newKeyword.trim()}
                      icon={<PiPlusLight className="h-4 w-4" />}
                    >
                      {t("Add")}
                    </Button>
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-3 border-t border-gray-100 dark:border-gray-700/60">
                  <Button variant="neutral" size="small" onClick={cancelEditing} disabled={isSaving}>
                    {t("Cancel")}
                  </Button>
                  <Button variant="primary" size="small" onClick={handleSave} disabled={isSaving}>
                    {isSaving ? t("Saving...") : t("Save Changes")}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="p-5">
                {ds?.description ? (
                  <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed whitespace-pre-wrap max-w-prose">
                    {ds.description}
                  </p>
                ) : (
                  <p className="text-sm text-gray-400 dark:text-gray-500 italic">
                    {t("No description available. A description will be auto-generated after indexing completes.")}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* ── Metadata Grid ── */}
          <div data-tour-id="ds-details-metadata" className="grid grid-cols-2 sm:grid-cols-4 gap-3">

            {/* Status */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm p-4">
              <div className="flex items-center gap-1.5 mb-2">
                <PiCircleDashedLight className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
                <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500">{t("Status")}</span>
              </div>
              <Badge color={st.badge as any}>{t(st.label)}</Badge>
            </div>

            {/* Indexer */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm p-4">
              <div className="flex items-center gap-1.5 mb-2">
                <PiArrowsClockwiseLight className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
                <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500">{t("Indexer")}</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge color={ix.badge as any}>{t(ix.label)}</Badge>
                {(ds?.indexer_status === 'running' || ds?.indexing_status === 'running') && (
                  <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-blue-400 border-t-transparent" />
                )}
              </div>
            </div>

            {/* Documents */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm p-4">
              <div className="flex items-center gap-1.5 mb-2">
                <PiFileLight className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
                <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500">{t("Documents")}</span>
              </div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                {String(ds?.document_count ?? 0)}
              </p>
            </div>

            {/* Total Size */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm p-4">
              <div className="flex items-center gap-1.5 mb-2">
                <PiDatabaseLight className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
                <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500">{t("Total Size")}</span>
              </div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                {formatSize(ds?.total_size_bytes)}
              </p>
            </div>
          </div>

          {/* ── Collection Details Grid ── */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">

            {/* Type */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm p-4">
              <div className="flex items-center gap-1.5 mb-2">
                <PiDiamondsFourLight className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
                <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500">{t("Type")}</span>
              </div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                {ds?.type || t("Not set")}
              </p>
            </div>

            {/* Language */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm p-4">
              <div className="flex items-center gap-1.5 mb-2">
                <PiGlobeLight className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
                <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500">{t("Language")}</span>
              </div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                {ds?.language || t("Not set")}
              </p>
            </div>

            {/* Visibility */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm p-4">
              <div className="flex items-center gap-1.5 mb-2">
                <PiEyeLight className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
                <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500">{t("Visibility")}</span>
              </div>
              <Badge color="gray">{t(visibilityLabel(ds))}</Badge>
            </div>

            {/* Source */}
            {/* <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm p-4">
              <div className="flex items-center gap-1.5 mb-2">
                <PiDatabaseLight className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
                <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500">{t("Source")}</span>
              </div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                {t("Blob Storage")}
              </p>
            </div> */}
          </div>

          {/* ── Activity Row ── */}
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div className="flex items-center gap-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm px-4 py-3">
              <PiCalendarBlankLight className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
              <div className="min-w-0">
                <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500">{t("Created")}</span>
                <p className="text-sm text-gray-700 dark:text-gray-200">{formatDate(ds?.created_at || ds?.CreatedAt)}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm px-4 py-3">
              <PiClockClockwiseLight className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
              <div className="min-w-0">
                <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500">{t("Updated")}</span>
                <p className="text-sm text-gray-700 dark:text-gray-200">{ds?.updated_at ? formatDateTime(ds.updated_at) : "—"}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm px-4 py-3">
              <PiArrowsClockwiseLight className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
              <div className="min-w-0">
                <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500">{t("Last Indexed")}</span>
                <p className="text-sm text-gray-700 dark:text-gray-200">{formatDateTime(ds?.last_indexed_at || ds?.last_index_run || undefined) || "—"}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm px-4 py-3">
              <PiLightningLight className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
              <div className="min-w-0">
                <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500">{t("AI Analysis")}</span>
                <p className="text-sm text-gray-700 dark:text-gray-200">{ds?.llm_generated_at ? formatDateTime(ds.llm_generated_at) : "—"}</p>
              </div>
            </div>
          </div>
        </div>

        {/* ── Right Column ── */}
        <div data-tour-id="ds-details-sidebar" className="space-y-5">

          {/* ── Keywords Card ── */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm">
            <div className="flex items-center gap-2 px-5 py-3.5 border-b border-gray-100 dark:border-gray-700/60">
              <PiTagLight className="h-4 w-4 text-gray-400 dark:text-gray-500" />
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{t("Keywords")}</span>
            </div>
            <div className="p-5">
              {(ds?.keywords?.length ?? 0) > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {ds!.keywords!.map((kw, i) => (
                    <Badge key={i} color="blue" className="capitalize">
                      {kw}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 dark:text-gray-500 italic">
                  {t("No keywords yet. Click Edit to add keywords, or they will be auto-generated after indexing.")}
                </p>
              )}
            </div>
          </div>

          {/* ── Supported Formats Card ── */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 shadow-sm">
            <div className="flex items-center gap-2 px-5 py-3.5 border-b border-gray-100 dark:border-gray-700/60">
              <PiFileTextLight className="h-4 w-4 text-gray-400 dark:text-gray-500" />
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{t("Supported Formats")}</span>
            </div>
            <div className="p-5">
              {fileTypes.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {fileTypes.map((ft, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center px-2.5 py-1 rounded-lg bg-gray-100 dark:bg-gray-700/50 text-xs font-mono font-medium text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700"
                    >
                      {ft}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 dark:text-gray-500 italic">
                  {t("Default file types: .pdf, .docx, .txt, .md")}
                </p>
              )}
            </div>
          </div>

          {/* Recent activity */}
          {(ds?.uuid || ds?.id) && (
            <RecentActivityCard
              fetchEntries={async (limit) => {
                const res: any = await dispatch(
                  fetchEntityAuditLog('DataSource', String(ds.uuid || ds.id), 1, limit) as any
                );
                return res?.data || [];
              }}
              onViewAll={() => setShowAuditLog(true)}
            />
          )}
        </div>
      </div>

      {showAuditLog && (ds?.uuid || ds?.id) && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/20 dark:bg-black/40 transition-opacity duration-300"
            onClick={() => setShowAuditLog(false)}
          />
          <div className="fixed inset-y-0 right-0 z-50 w-96 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 shadow-2xl flex flex-col animate-slide-in-right">
            <AuditLogPanel
              entityType="DataSource"
              entityId={String(ds.uuid || ds.id)}
              entityName={ds?.name || ds?.DataSourceName || ''}
              onClose={() => setShowAuditLog(false)}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default DataSourceDetailsPanel;
